import type { MixpanelService } from '~/core/service/mixpanel/mixpanel';

interface QueuedEvent {
	type: 'track' | 'register';
	args: unknown[];
}

// Queue to store events before Mixpanel is initialized
const eventQueue: QueuedEvent[] = [];
let isProcessingQueue = false;

/**
 * Safe Mixpanel composable that handles deferred initialization.
 * Events are queued if Mixpanel is not yet loaded and processed once it's ready.
 */
export function useMixpanel() {
	const nuxtApp = useNuxtApp();

	const getMixpanel = (): MixpanelService | undefined => {
		return nuxtApp.$mixpanel as MixpanelService | undefined;
	};

	const processQueue = () => {
		if (isProcessingQueue) return;

		const mixpanel = getMixpanel();
		if (!mixpanel?.isInitialized()) return;

		isProcessingQueue = true;

		while (eventQueue.length > 0) {
			const event = eventQueue.shift();
			if (!event) continue;

			try {
				if (event.type === 'track') {
					const [eventName, properties] = event.args as [
						string,
						Record<string, unknown>?
					];
					mixpanel.track(eventName, properties);
				} else if (event.type === 'register') {
					const [props, days] = event.args as [
						Record<string, unknown>,
						number?
					];
					mixpanel.register(props, days);
				}
			} catch (error) {
				console.error('Failed to process queued Mixpanel event:', error);
			}
		}

		isProcessingQueue = false;
	};

	// Try to process queue when called
	if (import.meta.client) {
		processQueue();
	}

	return {
		/**
		 * Track an event. Safe to call even before Mixpanel is initialized.
		 */
		track(eventName: string, properties?: Record<string, unknown>) {
			const mixpanel = getMixpanel();

			if (mixpanel?.isInitialized()) {
				mixpanel.track(eventName, properties);
			} else {
				// Queue the event for later
				eventQueue.push({ type: 'track', args: [eventName, properties] });

				// Try to process queue after a delay
				if (import.meta.client) {
					setTimeout(processQueue, 3000);
				}
			}
		},

		/**
		 * Register super properties. Safe to call even before Mixpanel is initialized.
		 */
		register(props: Record<string, unknown>, days?: number) {
			const mixpanel = getMixpanel();

			if (mixpanel?.isInitialized()) {
				mixpanel.register(props, days);
			} else {
				eventQueue.push({ type: 'register', args: [props, days] });

				if (import.meta.client) {
					setTimeout(processQueue, 3000);
				}
			}
		},

		/**
		 * Get anonymous ID. Returns null if Mixpanel is not yet initialized.
		 */
		getAnonymousId(): string | null {
			return getMixpanel()?.getAnonymousId() ?? null;
		},

		/**
		 * Alias user. Only works if Mixpanel is initialized.
		 */
		aliasUser(userId: string, anonymousId?: string) {
			const mixpanel = getMixpanel();
			if (mixpanel?.isInitialized()) {
				mixpanel.aliasUser(userId, anonymousId);
			}
		},

		/**
		 * Check if Mixpanel is initialized.
		 */
		isInitialized(): boolean {
			return getMixpanel()?.isInitialized() ?? false;
		},

		/**
		 * Get the raw Mixpanel service. May be undefined if not yet loaded.
		 */
		get instance(): MixpanelService | undefined {
			return getMixpanel();
		}
	};
}
